// ============================================================
// 📁 src/pages/Users/UsersPage.tsx   (جایگزین فایل قبلی)
// ============================================================
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { Users, UserPlus, Shield, Mail, ClipboardCheck, FileText, Activity, ClipboardList, Settings, Lock, Unlock, MoreVertical, Edit, Monitor, KeyRound, Smartphone, Pause, Play, Trash2, RotateCcw, ArrowRight, Download, Send, ShieldCheck, ShieldAlert, AlertTriangle, Check } from "lucide-react";
import { uapi, UserType, setServerOffset, stateLabel, roleLabel, relTime, fmtDT, downloadCSV } from "../../services/usersApi";
import { UmCtx, UmStyle, ToastHost, DialogHost, Tabs, Kpi, Sparkline, Badge, Avatar, Btn, SearchBox, Pager, Spinner, Empty, ActionMenu, stateColor, roleColor, toast, dlg, stepCall, notify, arr } from "./UsersUi";
import { InviteModal, EditModal, PermModal, SessionsModal, DeleteModal, TransferModal, Tpl } from "./UsersModals";
import { InvitationsTab, ApprovalsTab, TemplatesTab, AuditTab, ReviewTab, SettingsTab } from "./UsersTabs";
import { MySecurity } from "./UsersSecurity";

type Filter = "all" | "active" | "invited" | "suspended" | "inactive" | "deleted";

export default function UsersPage() {
  const { dark } = useTheme();
  const T: any = dark ? DARK : LIGHT;

  const [me, setMe] = useState<any>(null);
  const [users, setUsers] = useState<UserType[]>([]);
  const [dash, setDash] = useState<any>(null);
  const [templates, setTemplates] = useState<Tpl[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("users");
  const [pending, setPending] = useState(0);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [roleF, setRoleF] = useState("all");
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(25);
  const [sel, setSel] = useState<number[]>([]);

  const [showInvite, setShowInvite] = useState(false);
  const [editU, setEditU] = useState<UserType | null>(null);
  const [permU, setPermU] = useState<UserType | null>(null);
  const [sessU, setSessU] = useState<UserType | null>(null);
  const [delU, setDelU] = useState<UserType | null>(null);
  const [trU, setTrU] = useState<UserType | null>(null);

  const isAdmin = me?.user?.role === "admin";
  const canList = isAdmin || me?.user?.role === "manager";

  const loadMe = useCallback(async () => {
    const r = await uapi.me();
    if (r.success) { setServerOffset(r.server_offset || 0); setMe(r); return r; }
    return null;
  }, []);

  const loadAll = useCallback(async () => {
    const m = await loadMe();
    if (!m) { setLoading(false); return; }
    const admin = m.user.role === "admin";
    const lister = admin || m.user.role === "manager";
    const calls: Promise<any>[] = [];
    if (lister) calls.push(uapi.list(admin).then((r) => { if (r.success) setUsers(r.users); else toast(r.message || "خطا در دریافت کاربران", "err"); }));
    if (lister) calls.push(uapi.templates().then((r) => r.success && setTemplates(r.templates)));
    if (admin) calls.push(uapi.dashboard().then((r) => { if (r.success) { setDash(r); setPending(r.stats.pending_approvals); } }));
    else calls.push(uapi.approvals("inbox", "pending").then((r) => setPending(r?.counts?.pending || 0)));
    await Promise.all(calls);
    setLoading(false);
  }, [loadMe]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (me && !canList && tab === "users") setTab("approvals"); }, [me, canList, tab]);

  const live = useMemo(() => users.filter((u) => u.state !== "deleted"), [users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (filter === "deleted") { if (u.state !== "deleted") return false; }
      else if (u.state === "deleted") return false;
      if (filter === "active" && u.state !== "active") return false;
      if (filter === "invited" && u.state !== "invited" && u.state !== "invite_expired") return false;
      if (filter === "suspended" && u.state !== "suspended" && !u.locked) return false;
      if (filter === "inactive" && u.state !== "inactive") return false;
      if (roleF !== "all" && u.role !== roleF) return false;
      if (q && !(`${u.full_name} ${u.username} ${u.email} ${u.phone} ${u.department || ""} ${u.job_title || ""}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [users, search, filter, roleF]);

  useEffect(() => { setPage(1); setSel([]); }, [search, filter, roleF]);
  const rows = filtered.slice((page - 1) * per, page * per);

  /* ---------------- عملیات تکی ---------------- */
  const reload = () => loadAll();
  const setState = async (u: UserType, state: string) => {
    let reason = "";
    if (state === "suspended") { const n = await dlg.prompt("تعلیق کاربر", `دلیل تعلیق «${u.full_name}» را بنویسید`, "دلیل...", true); if (n === null) return; reason = n; }
    else if (state === "inactive" && !(await dlg.confirm("غیرفعال‌سازی", `«${u.full_name}» دیگر نمی‌تواند وارد شود و نشست‌های او بسته می‌شود.`, true))) return;
    const r = await uapi.setState(u.id, state, reason);
    if (notify(r)) reload();
  };
  const sendReset = async (u: UserType) => { if (!(await dlg.confirm("لینک بازیابی پسورد", `یک لینک امن به ایمیل «${u.email}» ارسال شود؟`))) return; const r = await uapi.sendReset(u.id); if (r.success && r.emailSent === false) toast(r.message, "err"); else notify(r); };
  const forcePw = async (u: UserType) => { if (!(await dlg.confirm("الزام تغییر پسورد", `«${u.full_name}» در ورود بعدی باید پسورد جدید بگذارد.`))) return; const r = await uapi.forcePasswordChange(u.id); if (notify(r)) reload(); };
  const reset2fa = async (u: UserType) => { if (!(await dlg.confirm("ریست 2FA", `احراز هویت دو مرحله‌ای «${u.full_name}» ریست می‌شود (مثلاً گوشی را گم کرده).`, true))) return; const r = await stepCall((pw) => uapi.twofaReset(u.id, pw || "")); if (notify(r)) reload(); };
  const unlock = async (u: UserType) => { const r = await uapi.unlock(u.id); if (notify(r)) reload(); };
  const restore = async (u: UserType) => { const r = await uapi.restore(u.id); if (notify(r)) reload(); };
  const resendInvite = async (u: UserType) => { const r = await uapi.resendInvite(u.id); if (r.success && !r.emailSent) toast(r.message, "err"); else notify(r); reload(); };

  /* ---------------- عملیات گروهی ---------------- */
  const bulk = async (op: string, label: string, extra: any = {}) => {
    if (!sel.length) return;
    if (op === "suspend") { const n = await dlg.prompt("تعلیق گروهی", "دلیل را بنویسید", "دلیل...", true); if (n === null) return; extra.reason = n; }
    else if (!(await dlg.confirm(label, `روی ${sel.length} کاربر انتخاب‌شده اعمال شود؟`, op !== "activate" && op !== "unlock" && op !== "apply_template"))) return;
    const r = await uapi.bulk(sel, op, extra);
    if (r.success) { const bad = arr(r, "results").filter((x: any) => !x.ok); toast(r.message + (bad.length ? ` — ${bad.length} مورد ناموفق: ${bad[0].message}` : ""), bad.length ? "err" : "ok"); setSel([]); reload(); } else notify(r);
  };

  const exportUsers = () => {
    downloadCSV(`users-${new Date().toISOString().slice(0, 10)}.csv`, ["نام", "نام کاربری", "ایمیل", "تلفن", "نقش", "وضعیت", "آخرین ورود", "2FA", "دپارتمان"],
      filtered.map((u) => [u.full_name, u.username, u.email, u.phone, roleLabel(u.role), stateLabel[u.state] || u.state, fmtDT(u.last_login), u.totp_enabled ? "فعال" : "خاموش", u.department || ""]));
    toast("خروجی ساخته شد", "ok");
  };

  const allSel = rows.length > 0 && rows.every((u) => sel.includes(u.id));
  const toggleAll = () => setSel(allSel ? sel.filter((id) => !rows.some((u) => u.id === id)) : Array.from(new Set([...sel, ...rows.map((u) => u.id)])));

  const permSummary = (u: UserType) => {
    if (u.role === "admin") return <Badge color="#f59e0b">دسترسی کامل</Badge>;
    const p = u.permissions || {};
    const v = Object.keys(p).filter((k) => p[k].view).length;
    const a = Object.keys(p).filter((k) => p[k].approve).length;
    return <span style={{ fontSize: 12 }}>{v} بخش{a ? <span style={{ color: "#d4af37", fontWeight: 700 }}> • {a} تایید</span> : null}{u.active_grants ? <span style={{ color: "#3b82f6" }}> • {u.active_grants} موقت</span> : null}</span>;
  };

  /* ---------------- تب‌ها ---------------- */
  const invitedCount = users.filter((u) => u.state === "invited" || u.state === "invite_expired").length;
  const tabs = [
    ...(canList ? [{ key: "users", label: "کاربران", icon: <Users size={15} /> }] : []),
    ...(isAdmin ? [{ key: "invitations", label: "دعوت‌ها", icon: <Mail size={15} />, badge: invitedCount }] : []),
    { key: "approvals", label: "تاییدها", icon: <ClipboardCheck size={15} />, badge: pending },
    ...(isAdmin ? [
      { key: "templates", label: "قالب‌های نقش", icon: <FileText size={15} /> },
      { key: "audit", label: "گزارش فعالیت و ورود", icon: <Activity size={15} /> },
      { key: "review", label: "بازبینی دسترسی", icon: <ClipboardList size={15} /> },
      { key: "settings", label: "تنظیمات", icon: <Settings size={15} /> },
    ] : []),
    { key: "security", label: "امنیت من", icon: <ShieldCheck size={15} /> },
  ];
  const st = dash?.stats;
  const series: number[] = arr(dash, "series").map((s: any) => +s.logins || 0);
  const chips: [Filter, string][] = [["all", "همه"], ["active", "فعال"], ["invited", "دعوت‌شده"], ["suspended", "معلق / قفل"], ["inactive", "غیرفعال"], ...(isAdmin ? [["deleted", "بایگانی"] as [Filter, string]] : [])];

  if (loading) return <div className={`um ${dark ? "dk" : "lt"}`} style={{ padding: 28, color: T.text }}><UmStyle /><Spinner /></div>;
  if (!me) return <div className={`um ${dark ? "dk" : "lt"}`} style={{ padding: 28, color: T.text }}><UmStyle /><Empty icon={<ShieldAlert size={44} />} title="دریافت اطلاعات ممکن نشد" sub="دوباره وارد شوید یا صفحه را تازه کنید" /></div>;

  return (
    <UmCtx.Provider value={{ dark, T }}>
      <div className={`um ${dark ? "dk" : "lt"}`} style={{ padding: "28px 28px 60px", color: T.text }}>
        <UmStyle /><ToastHost /><DialogHost />

        {/* ----------- سربرگ ----------- */}
        <div className="um-hero">
          <div className="um-gold-line" />
          <div className="um-blob b1" /><div className="um-blob b2" /><div className="um-blob b3" />
          <div style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 18 }}>
            <div>
              <div className="um-eyebrow">SECURITY & ACCESS CONTROL</div>
              <h1 className="um-title">مدیریت کاربران</h1>
              <p style={{ margin: "8px 0 0", fontSize: 13, opacity: 0.7, maxWidth: 560, lineHeight: 1.9 }}>دعوت امن با ایمیل، کنترل دقیق دسترسی‌ها و تاییدها، احراز هویت دو مرحله‌ای و ثبت کامل فعالیت‌ها</p>
            </div>
            {isAdmin && <Btn kind="gold" icon={<UserPlus size={16} />} onClick={() => setShowInvite(true)}>دعوت کاربر جدید</Btn>}
          </div>
          {isAdmin && series.length > 1 && <div style={{ position: "relative", marginTop: 18, maxWidth: 360, opacity: 0.95 }}><div className="um-hint" style={{ marginBottom: 2 }}>ورودهای ۱۴ روز اخیر</div><Sparkline data={series} color="#d4af37" /></div>}
        </div>

        {/* ----------- آمار ----------- */}
        {isAdmin && st && (
          <div className="um-kpis">
            <Kpi i={0} label="کل کاربران" value={st.total} color="#3b82f6" icon={<Users size={18} color="#3b82f6" />} />
            <Kpi i={1} label="فعال" value={st.active} color="#10b981" icon={<Check size={18} color="#10b981" />} />
            <Kpi i={2} label="دعوت در انتظار" value={st.invited + st.invite_expired} color="#8b5cf6" icon={<Mail size={18} color="#8b5cf6" />} />
            <Kpi i={3} label="در انتظار تایید" value={st.pending_approvals} color="#d4af37" icon={<ClipboardCheck size={18} color="#d4af37" />} />
            <Kpi i={4} label="با 2FA" value={st.with_2fa} color="#06b6d4" icon={<Smartphone size={18} color="#06b6d4" />} />
            <Kpi i={5} label="بدون 2FA" value={st.no_2fa} color="#f59e0b" icon={<ShieldAlert size={18} color="#f59e0b" />} />
            <Kpi i={6} label="ورود ناموفق ۲۴ ساعت" value={st.failed_24h} color="#ef4444" icon={<AlertTriangle size={18} color="#ef4444" />} />
            <Kpi i={7} label="نشست فعال" value={st.active_sessions} color="#14b8a6" icon={<Monitor size={18} color="#14b8a6" />} />
          </div>
        )}

        <Tabs tabs={tabs} active={tab} onChange={setTab} />

        {/* ----------- کاربران ----------- */}
        {tab === "users" && canList && (
          <div className="um-card gold">
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
              <SearchBox value={search} onChange={setSearch} placeholder="جستجوی نام، نام کاربری، ایمیل، تلفن..." />
              <select className="um-select" style={{ width: 130 }} value={roleF} onChange={(e) => setRoleF(e.target.value)}>
                <option value="all">همه نقش‌ها</option><option value="admin">ادمین</option><option value="manager">مدیر</option><option value="user">کاربر</option>
              </select>
              <Btn kind="ghost" size="sm" icon={<Download size={14} />} onClick={exportUsers}>خروجی CSV</Btn>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
              {chips.map(([k, l]) => <button key={k} className={`um-fchip ${filter === k ? "on" : ""}`} onClick={() => setFilter(k)}>{l}</button>)}
            </div>

            {isAdmin && sel.length > 0 && (
              <div className="um-card" style={{ padding: "12px 16px", marginBottom: 14, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", borderColor: "rgba(59,130,246,.45)" }}>
                <b style={{ fontSize: 13 }}>{sel.length} کاربر انتخاب شده</b>
                <Btn size="sm" kind="success" onClick={() => bulk("activate", "فعال‌سازی گروهی")}>فعال</Btn>
                <Btn size="sm" kind="ghost" onClick={() => bulk("deactivate", "غیرفعال‌سازی گروهی")}>غیرفعال</Btn>
                <Btn size="sm" kind="ghost" onClick={() => bulk("suspend", "تعلیق گروهی")}>تعلیق</Btn>
                <Btn size="sm" kind="ghost" onClick={() => bulk("unlock", "باز کردن قفل")}>باز کردن قفل</Btn>
                <Btn size="sm" kind="ghost" onClick={() => bulk("revoke_sessions", "بستن نشست‌ها")}>بستن نشست‌ها</Btn>
                <select className="um-select" style={{ width: 170 }} value="" onChange={(e) => e.target.value && bulk("apply_template", "اعمال قالب دسترسی", { templateId: +e.target.value })}>
                  <option value="">اعمال قالب دسترسی...</option>{templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <span style={{ flex: 1 }} /><Btn size="sm" kind="ghost" onClick={() => setSel([])}>لغو انتخاب</Btn>
              </div>
            )}

            {rows.length === 0 ? <Empty icon={<Users size={46} />} title="کاربری پیدا نشد" sub="فیلترها را تغییر دهید" /> : (
              <div className="um-scroll">
                <table className="um-tbl">
                  <thead><tr>
                    {isAdmin && <th style={{ width: 36 }}><button className={`um-cb ${allSel ? "on" : ""}`} onClick={toggleAll} aria-label="انتخاب همه">{allSel && <Check size={13} strokeWidth={3.5} />}</button></th>}
                    <th>کاربر</th><th>نقش</th><th>وضعیت</th><th>امنیت</th><th>دسترسی</th><th>آخرین ورود</th><th />
                  </tr></thead>
                  <tbody>
                    {rows.map((u, i) => {
                      const mine = u.id === me.user.id;
                      return (
                        <tr key={u.id} className={sel.includes(u.id) ? "sel" : ""} style={{ ["--i" as any]: i, opacity: u.state === "deleted" ? 0.6 : 1 }}>
                          {isAdmin && <td><button className={`um-cb ${sel.includes(u.id) ? "on" : ""}`} disabled={mine} onClick={() => setSel(sel.includes(u.id) ? sel.filter((x) => x !== u.id) : [...sel, u.id])}>{sel.includes(u.id) && <Check size={13} strokeWidth={3.5} />}</button></td>}
                          <td>
                            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                              <Avatar name={u.full_name} src={u.avatar} size={42} />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 700 }}>{u.full_name} {mine && <span className="um-hint">(شما)</span>}</div>
                                <div className="um-hint" style={{ margin: 0 }}>@{u.username}{u.email ? ` — ${u.email}` : ""}</div>
                                {(u.job_title || u.department) && <div className="um-hint" style={{ margin: 0 }}>{[u.job_title, u.department].filter(Boolean).join(" • ")}</div>}
                              </div>
                            </div>
                          </td>
                          <td><Badge color={roleColor(u.role)}>{roleLabel(u.role)}</Badge></td>
                          <td>
                            <Badge color={stateColor(u.state)} dot>{stateLabel[u.state] || u.state}</Badge>
                            {u.locked && <div style={{ marginTop: 4 }}><Badge color="#ef4444"><Lock size={11} /> قفل موقت</Badge></div>}
                          </td>
                          <td>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                              {u.totp_enabled ? <Badge color="#10b981"><Smartphone size={11} /> 2FA</Badge> : <Badge color="#64748b">بدون 2FA</Badge>}
                              {u.must_change_password && <Badge color="#f59e0b">تغییر پسورد</Badge>}
                              {!!u.sessions_count && <span className="um-hint" style={{ margin: 0 }}>{u.sessions_count} نشست</span>}
                            </div>
                          </td>
                          <td>{permSummary(u)}</td>
                          <td><div style={{ fontSize: 12 }}>{u.last_login ? relTime(u.last_login) : "هرگز"}</div></td>
                          <td>
                            <ActionMenu trigger={<MoreVertical size={15} />} items={isAdmin ? (u.state === "deleted" ? [
                              { label: "بازیابی کاربر", icon: <RotateCcw size={14} />, onClick: () => restore(u) },
                            ] : [
                              { label: "ویرایش", icon: <Edit size={14} />, onClick: () => setEditU(u) },
                              { label: "دسترسی‌ها", icon: <Shield size={14} />, onClick: () => setPermU(u), hidden: false },
                              { label: "نشست‌ها", icon: <Monitor size={14} />, onClick: () => setSessU(u), hidden: u.state === "invited" || u.state === "invite_expired" },
                              "-",
                              { label: "ارسال مجدد دعوت", icon: <Send size={14} />, onClick: () => resendInvite(u), hidden: !(u.state === "invited" || u.state === "invite_expired") },
                              { label: "ارسال لینک بازیابی پسورد", icon: <Mail size={14} />, onClick: () => sendReset(u), hidden: u.state === "invited" || u.state === "invite_expired" || !u.email },
                              { label: "الزام تغییر پسورد", icon: <KeyRound size={14} />, onClick: () => forcePw(u), hidden: u.state === "invited" || u.state === "invite_expired" },
                              { label: "ریست 2FA", icon: <Smartphone size={14} />, onClick: () => reset2fa(u), hidden: !u.totp_enabled },
                              { label: "باز کردن قفل", icon: <Unlock size={14} />, onClick: () => unlock(u), hidden: !u.locked },
                              "-",
                              { label: "فعال‌سازی", icon: <Play size={14} />, onClick: () => setState(u, "active"), hidden: u.state === "active" || u.state === "invited" || u.state === "invite_expired" },
                              { label: "تعلیق", icon: <Pause size={14} />, onClick: () => setState(u, "suspended"), hidden: mine || u.state !== "active" },
                              { label: "غیرفعال‌سازی", icon: <Lock size={14} />, onClick: () => setState(u, "inactive"), hidden: mine || u.state !== "active" },
                              { label: "انتقال مالکیت کارها", icon: <ArrowRight size={14} />, onClick: () => setTrU(u), hidden: u.state === "invited" || u.state === "invite_expired" },
                              { label: "بایگانی (حذف)", icon: <Trash2 size={14} />, danger: true, onClick: () => setDelU(u), hidden: mine || u.id === 1 },
                            ]) : [
                              { label: "مشاهده دسترسی‌ها", icon: <Shield size={14} />, onClick: () => setPermU(u) },
                            ]} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <Pager total={filtered.length} page={page} per={per} onPage={setPage} onPer={setPer} />
          </div>
        )}

        {tab === "invitations" && isAdmin && <InvitationsTab users={users} reload={reload} onInvite={() => setShowInvite(true)} />}
        {tab === "approvals" && <ApprovalsTab isAdmin={isAdmin} users={live} onChanged={reload} />}
        {tab === "templates" && isAdmin && <TemplatesTab templates={templates} reload={reload} />}
        {tab === "audit" && isAdmin && <AuditTab users={live} />}
        {tab === "review" && isAdmin && <ReviewTab onChanged={reload} />}
        {tab === "settings" && isAdmin && <SettingsTab />}
        {tab === "security" && <MySecurity key={me.user.id + String(me.user.totp_enabled)} me={me.user} minLen={me.policy?.pw_min_length || 10} twoFaRequired={!!me.policy?.two_factor_required} onChanged={loadMe} />}

        {/* ----------- پنجره‌ها ----------- */}
        {showInvite && <InviteModal templates={templates} onClose={() => setShowInvite(false)} onDone={reload} />}
        {editU && <EditModal user={editU} onClose={() => setEditU(null)} onDone={reload} />}
        {permU && <PermModal user={permU} users={live} templates={templates} onClose={() => setPermU(null)} onDone={reload} />}
        {sessU && <SessionsModal user={sessU} onClose={() => setSessU(null)} onDone={reload} />}
        {delU && <DeleteModal user={delU} users={live} onClose={() => setDelU(null)} onDone={reload} />}
        {trU && <TransferModal user={trU} users={live} onClose={() => setTrU(null)} onDone={reload} />}
      </div>
    </UmCtx.Provider>
  );
}
